/**
 * RenderingCantiere — sezione dedicata sulla landing al case d'uso più forte:
 * partire da una stanza grezza in costruzione (intonaco, fili a vista,
 * pavimento crudo) e arrivare a un render fotorealistico arredato in stile.
 *
 * iter v31 r63 — User: «mandami una preview, sezione dedicata solo al
 * rendering, chiaramente che si può scegliere lo stile e tutto quello che
 * facciamo. Le immagini te le mando io, sono cantiere reale + render scandi.»
 *
 * Posizione: subito dopo FotoMega (11 stanze), prima di VecchioMetodo.
 * È un'amplificazione visiva del "Arreda" appena visto: il caso estremo.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { ArrowRight, HardHat, Sparkle } from "@phosphor-icons/react";

const STILI = [
  { v: "moderno", l: "Moderno", body: "Linee pulite, materiali neutri, pochi accenti." },
  { v: "classico", l: "Classico", body: "Boiserie, legno scuro, tessuti caldi." },
  { v: "minimalista", l: "Minimal", body: "Solo l'essenziale, ariosità totale." },
  { v: "scandinavo", l: "Scandinavo", body: "Legni chiari, lino, verde naturale." },
];

export default function RenderingCantiere() {
  const navigate = useNavigate();
  return (
    <section
      id="rendering-cantiere"
      className="py-16 sm:py-24 bg-[#0F1410] text-white relative overflow-hidden"
      data-testid="feature-rendering-cantiere"
    >
      {/* Texture grain sottile per evitare il "piatto" del nero */}
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 relative">
        {/* Header */}
        <div className="max-w-2xl mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7C8B6E]/15 border border-[#7C8B6E]/30">
            <HardHat size={13} weight="bold" className="text-[#A8C29A]" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#A8C29A]">
              Render dal cantiere
            </span>
          </div>

          <h2
            className="font-editorial text-[34px] sm:text-[44px] lg:text-[56px] mt-5 leading-[1.02] tracking-tight"
            data-testid="rendering-cantiere-title"
          >
            Scegli lo stile, scegli l&apos;ambiente.{" "}
            <span className="italic text-[#A8C29A]">In un click.</span>
          </h2>

          <p className="mt-5 text-[16px] sm:text-[18px] text-white/75 leading-relaxed">
            Carichi una foto dello spazio grezzo — fili elettrici a vista,
            intonaco crudo, pavimento da gettare — dici come lo vuoi e Pronto
            te lo restituisce arredato e fotorealistico. Stessi muri, stesse
            finestre, stessa luce della foto originale.
          </p>
        </div>

        {/* Before / After slider — centerpiece */}
        <div
          className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
          data-testid="rendering-cantiere-slider"
        >
          <BeforeAfterSlider
            beforeSrc="/landing/cantiere-before.jpg"
            afterSrc="/landing/cantiere-after.jpg"
            height="h-[70vh]"
            autoPlay
            objectPosition="68% center"
          />
        </div>

        {/* 4 stili applicabili */}
        <div className="mt-12 sm:mt-16">
          <div className="flex items-center gap-2 mb-5">
            <Sparkle size={14} weight="fill" className="text-[#A8C29A]" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-white/55">
              Quattro stili tra cui scegliere
            </span>
          </div>

          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
            data-testid="rendering-cantiere-stili"
          >
            {STILI.map((s) => (
              <div
                key={s.v}
                className="rounded-2xl border border-white/12 bg-white/[0.03] hover:bg-white/[0.06] transition-colors p-4 sm:p-5"
                data-testid={`rendering-stile-${s.v}`}
              >
                <div className="font-editorial text-[18px] sm:text-[20px] text-white leading-tight">
                  {s.l}
                </div>
                <p className="mt-2 text-[12.5px] text-white/55 leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bullet point onesti — cosa fa e cosa NON fa */}
        <div className="mt-10 sm:mt-14 grid sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="rounded-2xl bg-[#7C8B6E]/10 border border-[#7C8B6E]/25 p-5">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#A8C29A] mb-3">
              Cosa cambia
            </div>
            <ul className="space-y-2 text-[13.5px] text-white/80 leading-relaxed">
              <li>· Mobili, tessili, piante, luci, opere d&apos;arte</li>
              <li>· Finitura del pavimento, colore delle pareti</li>
              <li>· Atmosfera (luce calda della sera, sole del mattino)</li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-white/55 mb-3">
              Cosa NON cambia
            </div>
            <ul className="space-y-2 text-[13.5px] text-white/65 leading-relaxed">
              <li>· La pianta, i muri portanti, i passaggi tra le stanze</li>
              <li>· Le finestre (posizione, dimensione, infissi)</li>
              <li>· Le porte, gli attacchi degli impianti</li>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 sm:mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/prova")}
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#F1F4ED] text-[#0F1410] px-6 py-3.5 rounded-xl font-semibold text-[14.5px] transition-colors active:scale-[0.98]"
            data-testid="rendering-cantiere-cta"
          >
            Provala su una tua foto
            <ArrowRight size={16} weight="bold" />
          </button>
          <span className="text-[12px] text-white/55">
            Senza registrarti · scegli lo stile · vedi il risultato dal browser
          </span>
        </div>
      </div>
    </section>
  );
}
